/**
 * Within-scan floor-y noise reduction.
 *
 * RoomPlan's per-room floor y values disagree across rooms on the
 * same physical slab by 1-15 cm — pure capture noise, not real
 * design steps. This pass single-link clusters rooms whose floor y
 * values lie within `MIN_REAL_STEP_M` (7 inches = 0.1778 m, one
 * standard residential stair tread) of each other and replaces each
 * cluster's rooms with the median of the cluster's y values.
 * Singletons (rooms whose y differs from every other room by >=
 * MIN_REAL_STEP_M) pass through unchanged so genuine multi-level steps
 * (half-flights, second stories, ≥7" sunken rooms) are preserved.
 *
 * Trade-off: steps under 7" are erased — a 5" sunken living room
 * within 7" of the main slab gets clustered with the slab and loses
 * its step. Acceptable because (a) RoomPlan rarely captures a sub-7"
 * step accurately and (b) cross-room equalization is out of scope.
 * Where the DEPTH measured one, Step 3.5c puts it back after this
 * pass (`split-level/reconstructSmallSteps.js`).
 *
 * Plan: claude-docs/2026-06-01-within-scan-floor-y-reconciliation.md
 *
 * The function mutates each room's `floors[0].polygonCorners[*].y`
 * and `floors[0].center.y` in place; XZ is untouched. Returns
 * `{ clusters, stats }` diagnostics — the input `rooms` array is the
 * caller's source of truth post-call.
 *
 * Objects ride along. Two passes in this module move a floor plane after
 * ingest — `reseatDetachedFloors` and this one — and each is a place the
 * "objects stand on their room's floor" invariant established by
 * `seatRoomObjectsOnFloor` can break: clustering a room onto a shared
 * median moves the floor out from under furniture that was just seated on
 * it (985E18C8 room 5, -1.219 → -1.341). Every object takes the same
 * signed shift as its floor, so an object at a genuine offset — a wall
 * unit, a chair on a raised half-level — keeps that offset. Anything else
 * added here that writes a plate must carry the objects too.
 */

// Anything < this between two rooms' floor y values is treated as
// RoomPlan capture noise (same slab); anything >= is a real step
// (half-flight, ≥7" sunken room, story change). 7" = one standard
// residential stair tread.
const MIN_REAL_STEP_M = 0.1778;
// FP cushion: 7" + tiny float noise should still register as a real
// step at the strict-< boundary. Subtracted from the threshold inside
// the cluster check; not a tunable knob.
const STEP_BOUNDARY_FP_CUSHION_M = 1e-9;

/**
 * Largest floor-vs-wall disagreement treated as the merge's within-room
 * offset. Beyond it the two numbers disagree about which *level* the room
 * is on, which is #1042's work — absorbing that would quietly paper over a
 * broken scan.
 *
 * Owned here because TWO passes need the same line. `seatRoomObjectsOnFloor`
 * refuses to lift a room's objects past it, and `reseatDetachedFloors` must
 * therefore refuse to carry them back past it — the plate still moves (that
 * restoration is unconditional), but objects nothing lifted must not be
 * dragged down with it. Split thresholds here would silently reintroduce the
 * asymmetry this module exists to remove.
 *
 * Corpus-measured at the stage this runs, across 133 rooms in all 36
 * aligned-structure scans: legitimate within-room offsets top out at
 * 0.178 m (F6D83996 room1), the nearest level error is 0.620 m
 * (FBE0185D room1, "3rd Story"), and the 3.5x-wide band between them is
 * empty. 0.3 m is `FLOOR_BELOW_TOLERANCE` from
 * `functions/features/post-processing/helpers.js` — the same number that
 * currently *compensates* for this inconsistency downstream — and it sits
 * 1.7x above the largest real offset and 2.1x below the smallest level
 * error.
 *
 * `MIN_REAL_STEP_M` (0.1778) is deliberately NOT the cap: it bisects the
 * data at its worst point, rejecting F6D83996 room1's legitimate 0.1784
 * offset by 0.64 mm.
 */
const MAX_ROOM_FLOOR_OFFSET_M = 0.3;

function readFloorY(room) {
  const corners = room?.floors?.[0]?.polygonCorners;
  if (Array.isArray(corners)) {
    for (const c of corners) {
      if (Number.isFinite(c?.y)) return c.y;
    }
  }
  const cy = room?.floors?.[0]?.center?.y;
  return Number.isFinite(cy) ? cy : null;
}

/** Set a room's floor plate to `y` (plate corners + center), in place. */
function writeFloorY(room, y) {
  const floor = room?.floors?.[0];
  if (!floor) return;
  if (Array.isArray(floor.polygonCorners)) {
    for (const c of floor.polygonCorners) {
      if (c) c.y = y;
    }
  }
  if (floor.center) {
    floor.center.y = y;
  } else {
    floor.center = { y };
  }
}

/** Base (bottom) y of a wall, from its plate corners or center/height. */
function wallBaseY(wall) {
  const corners = wall?.polygonCorners;
  if (Array.isArray(corners) && corners.length) {
    const ys = corners.map((c) => c?.y).filter(Number.isFinite);
    if (ys.length) return Math.min(...ys);
  }
  const cy = wall?.center?.y;
  const h = wall?.dimensions?.y;
  if (!Number.isFinite(cy) || !Number.isFinite(h)) return null;
  return cy - h / 2;
}

function median(xs) {
  const s = xs.slice().sort((a, b) => a - b);
  if (s.length === 0) return null;
  const m = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[m - 1] + s[m]) / 2 : s[m];
}

/**
 * Shift every object's center Y by `delta`, in place. XZ untouched, and
 * relative heights within the room are preserved because the shift is
 * uniform. Returns the number of objects moved.
 */
function shiftObjectsY(objects, delta) {
  if (!Array.isArray(objects) || !Number.isFinite(delta)) return 0;
  let moved = 0;
  for (const object of objects) {
    if (!Number.isFinite(object?.center?.y)) continue;
    object.center.y += delta;
    moved++;
  }
  return moved;
}

/**
 * Single-link cluster: rooms a and b are linked iff
 * |y(a) − y(b)| < threshold. Returns an array of clusters, each
 * cluster being an array of room indices (into the input array).
 */
function clusterByY(ys, threshold) {
  const visited = new Array(ys.length).fill(false);
  const clusters = [];
  for (let i = 0; i < ys.length; i++) {
    if (visited[i]) continue;
    visited[i] = true;
    const queue = [i];
    const cluster = [];
    while (queue.length) {
      const k = queue.shift();
      cluster.push(k);
      for (let j = 0; j < ys.length; j++) {
        if (visited[j]) continue;
        if (Math.abs(ys[k] - ys[j]) < threshold) {
          visited[j] = true;
          queue.push(j);
        }
      }
    }
    clusters.push(cluster);
  }
  return clusters;
}

/**
 * Re-seat floor plates RoomPlan's merge detached from their own walls.
 *
 * In a raw capture a room's floor sits EXACTLY at its walls' base — across 108
 * rooms in 37 real dev scans the offset is 0.000 m, without exception. The
 * on-device merge breaks that: folding each capture into the shared frame it
 * translates a room's walls and its floor plate independently, and can leave
 * the plate up to a metre off. On EC3C3390 room 1 the merge moves the walls
 * −0.588 m onto the main slab and the floor +0.401 m, detaching the plate by
 * 0.988 m from the walls that enclose it.
 *
 * Left alone the phantom level propagates: the CV polygon build gives every
 * sub-room traced from that capture the detached floor y, `aggregate-
 * StructureWallCorners` then clips the shared structure wall's base up to it,
 * and every opening below that line ends up outside its own host wall (four of
 * EC3C3390's ground-floor windows, three of them with sills under the base).
 *
 * **The restoration is unconditional — there is deliberately no "how detached
 * is too detached" threshold.** Thresholding it was tried and is actively
 * harmful: correcting only the worst plate in a structure leaves its neighbours
 * on their merge-nudged values, and the resulting inconsistency is worse than
 * the original. Measured over the corpus, openings left outside their own host
 * wall go 7 (uncorrected) → 7 (correct only offsets ≥ 7", which fixes EC3C3390
 * and FBE0185D but breaks F6D83996) → **0** (correct every plate). A floor
 * belongs on its walls; half of that rule is not a weaker version of it.
 *
 * Most corrections are centimetre-scale and wash straight out —
 * `reconcileWithinScanFloorY` re-clusters right after and overwrites each plate
 * with its slab's median anyway. What matters is that the plates enter that
 * clustering agreeing with their walls.
 *
 * Runs immediately BEFORE `reconcileWithinScanFloorY` so the corrected y takes
 * part in clustering. Mutates `rooms[*].floors[0]` and `rooms[*].objects` in
 * place; XZ untouched.
 *
 * **Objects are carried with the plate**, the same way `reconcileWithinScan-
 * FloorY` carries them. This is not optional here: `seatRoomObjectsOnFloor`
 * runs at ingest and lifts a room's objects onto its plate by exactly
 * `floorY - median(wall bottoms)` — measured off the same plane this pass
 * puts the plate back on. Moving the plate alone therefore cancels that
 * correction on the floor while leaving the objects displaced by the full
 * delta, every time both fire. On King's Drive `F6D83996` that left the
 * kitchen's 95" base-cabinet box 13.7 cm above its own floor, outside
 * `isOnFloor`'s 0.1 m above-tolerance, so it was stamped `storageMount:
 * 'wall'` and never became cabinetry (2.214.0).
 *
 * @param {Object[]} rooms - room docs with `floors[0]` + `walls`.
 * @param {Object} [opts]
 * @param {number} [opts.steppedWallsM] - a room whose own walls' bases span at
 *   least this much has no single base to seat against, and is skipped.
 * @param {number} [opts.maxCarriedOffsetM] - past this the plate still moves
 *   but the objects do not; must match `seatRoomObjectsOnFloor`'s own cap.
 * @returns {{ roomsReseated, roomsSkippedSteppedWalls, maxShiftM,
 *   objectsCarried, roomsCarryOverCap, notable }} `notable` holds only the
 *   plates that moved at least `MIN_REAL_STEP_M`.
 */
function reseatDetachedFloors(
  rooms,
  {
    steppedWallsM = MIN_REAL_STEP_M,
    maxCarriedOffsetM = MAX_ROOM_FLOOR_OFFSET_M,
  } = {}
) {
  // `notable` lists only the plates that moved a real step or more. Since the
  // restoration is unconditional almost every room lands in `roomsReseated`
  // (108 of 108 across the corpus), so a per-room list would be one entry per
  // room per scan — noise in the logs and in checkpoint metadata alike. The
  // few metre-scale corrections are what a reader actually wants to see.
  const stats = {
    roomsReseated: 0,
    roomsSkippedSteppedWalls: 0,
    maxShiftM: 0,
    objectsCarried: 0,
    roomsCarryOverCap: 0,
    notable: [],
  };

  for (const room of rooms || []) {
    const floorY = readFloorY(room);
    if (!Number.isFinite(floorY)) continue;

    const bases = (room.walls || []).map(wallBaseY).filter(Number.isFinite);
    if (bases.length < 2) continue;
    bases.sort((a, b) => a - b);

    // A room whose own walls disagree about where they start has no single
    // base to seat the floor against. That does not happen in merged RoomPlan
    // input (max spread 0.021 m across the corpus), but our own split-level
    // and double-height reconstruction produces exactly that shape later, so
    // the guard keeps this pass honest if it is ever moved downstream.
    const spread = bases[bases.length - 1] - bases[0];
    if (spread >= steppedWallsM) {
      stats.roomsSkippedSteppedWalls += 1;
      continue;
    }

    const baseY = bases[Math.floor(bases.length / 2)];
    const offset = floorY - baseY;
    if (Math.abs(offset) < 1e-9) continue; // already seated

    writeFloorY(room, baseY);
    // Carry the objects, but only over the range the ingest seat covers.
    // `seatRoomObjectsOnFloor` lifted them onto this plate by exactly
    // `floorY - median(wall bottoms)`, measured off the very plane we are
    // putting the plate back on — so moving the plate without them leaves
    // every object that far off its own floor, and the two passes cancel to
    // a guaranteed error rather than a fix. Past the cap that pass REFUSES
    // (the room's floor and walls disagree about which level it is on), so
    // there is nothing to carry back and dragging the objects down with the
    // plate would invent the displacement instead of undoing it.
    if (Math.abs(offset) <= maxCarriedOffsetM) {
      stats.objectsCarried += shiftObjectsY(room.objects, -offset);
    } else {
      stats.roomsCarryOverCap += 1;
    }
    stats.roomsReseated += 1;
    stats.maxShiftM = Math.max(stats.maxShiftM, Math.abs(offset));
    if (Math.abs(offset) >= MIN_REAL_STEP_M) {
      stats.notable.push({
        roomId: room.id ?? null,
        from: +floorY.toFixed(3),
        to: +baseY.toFixed(3),
      });
    }
  }

  return stats;
}

function reconcileWithinScanFloorY(
  rooms,
  { thresholdM = MIN_REAL_STEP_M } = {}
) {
  const effectiveThreshold = thresholdM - STEP_BOUNDARY_FP_CUSHION_M;
  const indexed = [];
  for (let i = 0; i < (rooms || []).length; i++) {
    const y = readFloorY(rooms[i]);
    if (Number.isFinite(y)) indexed.push({ i, y });
  }

  const ys = indexed.map((r) => r.y);
  const clusterIdxs = clusterByY(ys, effectiveThreshold);

  const clusters = [];
  let roomsAdjusted = 0;
  let maxShiftM = 0;
  let objectsCarried = 0;
  for (const cluster of clusterIdxs) {
    const cy = cluster.map((k) => ys[k]);
    const clusterY = median(cy);
    const memberRoomIdxs = cluster.map((k) => indexed[k].i);
    for (const k of cluster) {
      const ri = indexed[k].i;
      const room = rooms[ri];
      const floor = room.floors[0];
      // Signed, and measured against the y the clustering itself read,
      // so the objects move exactly as far as the plane they stand on.
      const delta = clusterY - ys[k];
      const shift = Math.abs(delta);
      if (shift > 1e-6) {
        roomsAdjusted++;
        if (shift > maxShiftM) maxShiftM = shift;
        objectsCarried += shiftObjectsY(room.objects, delta);
      }
      writeFloorY(room, clusterY);
    }
    clusters.push({
      roomIds: memberRoomIdxs.map((ri) => rooms[ri]?.id ?? null),
      yBefore: cy,
      yAfter: clusterY,
    });
  }

  return {
    clusters,
    stats: {
      clustersFound: clusters.length,
      roomsAdjusted,
      maxShiftM,
      objectsCarried,
    },
  };
}

module.exports = {
  MAX_ROOM_FLOOR_OFFSET_M,
  reconcileWithinScanFloorY,
  shiftObjectsY,
  readFloorY,
  median,
  reseatDetachedFloors,
  MIN_REAL_STEP_M,
  // exposed for testing
  _internal: { readFloorY, writeFloorY, wallBaseY, median, clusterByY },
};
